import { useState, useRef } from 'react'

/**
 * Shared validate -> commit -> poll state machine for the shipping-costs bulk
 * wizards. Both update and create use the same async contract:
 *   dry_run:true  -> 200/422 { valid, row_errors }   (synchronous validation)
 *   dry_run:false -> 202 { job_id }                   (commit, then poll)
 *
 * `submitFn` is the service call (bulkUpdateShippingCosts / bulkCreateShippingCosts);
 * it already returns the 422 body instead of throwing.
 *
 * status: "editing" | "validating" | "validated" | "committing" | "running"
 */
export const useBulkCostJob = submitFn => {
  const [status, setStatus] = useState('editing')
  const [validation, setValidation] = useState(null)
  const [jobId, setJobId] = useState(null)
  const [error, setError] = useState(null)
  // Monotonic token so an out-of-order dry-run response (or one that resolves after the
  // rows/bindings changed) can't overwrite the current validation state.
  const validateToken = useRef(0)

  const validate = async payload => {
    const token = ++validateToken.current
    setStatus('validating')
    setError(null)
    try {
      const res = await submitFn({ ...payload, dry_run: true })
      if (token !== validateToken.current) return res // a newer validate/markDirty superseded this one
      setValidation(res)
      setStatus('validated')
      return res
    } catch (e) {
      if (token !== validateToken.current) return null
      setError(e?.response?.data?.error || 'request_failed')
      setStatus('editing')
      return null
    }
  }

  const commit = async payload => {
    setStatus('committing')
    setError(null)
    try {
      const res = await submitFn({ ...payload, dry_run: false })
      if (res?.job_id) {
        setJobId(res.job_id)
        setStatus('running')
      } else {
        // Backend rejected at commit time — surface it as a validation result.
        setValidation(res)
        setStatus('validated')
      }
      return res
    } catch (e) {
      setError(e?.response?.data?.error || 'request_failed')
      setStatus('validated')
      return null
    }
  }

  // Any edit to the rows invalidates a prior dry-run result (unless a job is
  // already running/committing, where the grid is hidden anyway).
  const markDirty = () => {
    validateToken.current++ // discard any in-flight dry-run: its result no longer matches the edited payload
    setStatus(s => (s === 'running' || s === 'committing' ? s : 'editing'))
    setValidation(null)
    setError(null)
  }

  const reset = () => {
    validateToken.current++ // cancel any in-flight dry-run (e.g. modal closed mid-validate)
    setStatus('editing')
    setValidation(null)
    setJobId(null)
    setError(null)
  }

  return { status, validation, jobId, error, validate, commit, markDirty, reset }
}
